// TODO: review Repeater implementation as initial solution was done not in the optimal way.
//  It should be rewritten.

import { IncludeTypeNumber, ProductAttributeKey } from "@gn-audio/core/common/enums";
import { ISearchItem } from '@gn-audio/core/api/product-catalogue/models';
import { ColorDotsService } from "./color-dots.service";

const enum LinkClass {
  Accessories = 'accessories-link',
  Support = 'support-link',
  Product = 'product-link',
  SearchResultBox = 'search-result__link',
  ProductInfoBox = 'search-result__products__info',
  ProductLabel = 'search-result__products__name__label',
}

const enum Attribute {
  Href = 'href',
  Target = 'target',
}

const enum PageUrlTypes {
  PrimaryPageUrl = 'primaryPageUrl',
  PageUrl = 'pageUrl',
  SupportPageUrl = 'supportPageUrl',
}

const hiddenClass = 'hidden';
const elementHiddenClass = 'element-' + hiddenClass;

const accessoryItemAttribute = `${ProductAttributeKey.MetaProductType}|8`;
const stub = 'javascript:;';

const colorDotsService: ColorDotsService = new ColorDotsService();

function isAccessoryItem<T extends ISearchItem>({ attributes = [] }: T): boolean {
  return attributes.some((x: string): boolean => x.toLowerCase() === accessoryItemAttribute.toLowerCase());
}

function isProductEOL<T extends ISearchItem>({ itemType, groupState, familyState }: T): boolean {
  switch (itemType) {
    case 'Family':
      return familyState < IncludeTypeNumber.Available;
    case 'Group':
      return groupState < IncludeTypeNumber.Available;
  }
}

function getItemUrl<T extends ISearchItem>(href: string, data: T) {
  if (isProductEOL(data) &&
    href === PageUrlTypes.PrimaryPageUrl &&
    data[PageUrlTypes.SupportPageUrl])
    return data[PageUrlTypes.SupportPageUrl];

  else if (href === PageUrlTypes.PrimaryPageUrl)
    return data[PageUrlTypes.PageUrl];

  return data[href];
}

function handleHref<T extends ISearchItem>(node: HTMLElement, data: T, languagePrefix: string | undefined) {
  let dataHrefValue: string | undefined = getItemUrl(node.dataset.href, data)
  const hrefSuffixValue: string | undefined = node.dataset.hrefSuffix;
  const hrefSuffixSkuValue: string | undefined = node.dataset.hrefSuffixSku;
  const dataSkuValue: string | undefined = data[hrefSuffixSkuValue];

  if (dataHrefValue && hrefSuffixValue) {
    dataHrefValue += hrefSuffixValue;
  }

  if (dataHrefValue && dataSkuValue) {
    dataHrefValue += `${hrefSuffixValue ? '' : '#/'}#${dataSkuValue}`;
  }

  if(addLanguagePrefix(dataHrefValue, languagePrefix)) {
    dataHrefValue = `/${languagePrefix}${dataHrefValue}`;
  }

  node.setAttribute(Attribute.Href, dataHrefValue ? dataHrefValue : stub);

  if (!dataHrefValue && node.hasAttribute(Attribute.Target)) {
    node.removeAttribute(Attribute.Target);
  }

  handleHiddenStatus(node, data, dataHrefValue);
}

function addLanguagePrefix(href: string, languagePrefix: string | undefined): boolean {

  if (!href || !languagePrefix) {
    return false;
  }

  if (href.startsWith('/') && !href.startsWith('//')) {
    return true;
  }

  return false;
}

function handleHiddenStatus<T extends ISearchItem>(node: HTMLElement, data: T, href: string) {
  const { parentElement } = node.parentElement;

  if (node.classList.contains(LinkClass.Product) && (!href || isProductEOL(data))) {
    parentElement.classList.add(`${LinkClass.Product}-${hiddenClass}`);
  }

  if (node.classList.contains(LinkClass.Support) && !href) {
    parentElement.classList.add(`${LinkClass.Support}-${hiddenClass}`);
  }

  if (node.classList.contains(LinkClass.Accessories) && (!href || isAccessoryItem(data))) {
    parentElement.classList.add(`${LinkClass.Accessories}-${hiddenClass}`);
  }

  if (node.classList.contains(LinkClass.SearchResultBox) && isProductEOL(data)) {
    handleDiscontinuedProduct(node);
  }
}

function handleDiscontinuedProduct(productItem: HTMLElement) {
  const labelItem = productItem.querySelector(
    `span[class="${LinkClass.ProductLabel} ${elementHiddenClass}"]`);

  if (!labelItem) {
    return;
  }

  labelItem.classList.remove(elementHiddenClass);
}

function handleColorDots<T>(node: HTMLElement, data: T) {
  const dataAttributesValue: string[] | undefined = data[node.dataset.attributes];

  if (!dataAttributesValue || ![0, "Family"].includes(data[node.dataset.itemType]))
    return;

  const colorDotsHTMLElement = colorDotsService.getColorDotsHTMLElement(dataAttributesValue);
  node.innerHTML += colorDotsHTMLElement;
}

export class Repeater<T extends ISearchItem> {
  private element: HTMLElement;
  private mainElement: Node;

  constructor(elementId: string) {
    this.element = document.getElementById(elementId);

    if (this.element === null) {
      return;
    }

    this.mainElement = this.element.cloneNode(true);
    this.clean();
  }

  public createElement(data: T[], languagePrefix: string | undefined) {
    this.clean();
    Array.prototype.forEach
      .call(data, (el: T) => {
        const node: HTMLElement = this.mainElement.cloneNode(true) as HTMLElement;
        this.recurseDomChildren(node, el, languagePrefix);
        Array.prototype.forEach
          .call(node.children, (n: HTMLElement) => {
            this.element.appendChild(n);
          });
      });
  }

  public clean() {
    this.element.innerHTML = '';
  }

  public recurseDomChildren(firstElement: HTMLElement, data: T, languagePrefix: string | undefined) {
    let nodes;
    if (firstElement.childNodes) {
      nodes = firstElement.childNodes;
      this.loopNodeChildren(nodes, data, languagePrefix);
    }
  }

  public loopNodeChildren(nodes: NodeListOf<HTMLElement>, data: T, languagePrefix: string | undefined) {
    Array.prototype.forEach
      .call(nodes, (node: HTMLElement) => {
        if (node.dataset) {
          if (node.dataset.info) {
            node.innerHTML = data[node.dataset.info] || data;
          }

          if (node.dataset.src) {
            let srcValue: string = data[node.dataset.src][0];
            if (typeof srcValue !== 'string' || srcValue === '') {
              srcValue = '/components/design/content/blank.png';
            }

            node.style.backgroundImage = `url(${srcValue})`;
          }

          if (node.dataset.href) {
            handleHref(node, data, languagePrefix);
          }

          if (node.dataset.attributes) {
            handleColorDots(node, data);
          }

          if (node.dataset.alt) {
            node.setAttribute('alt', data[node.dataset.alt] || '');
          }
        }

        if (node.childNodes) {
          this.recurseDomChildren(node, data, languagePrefix);
        }
      });
  }
}
